import React, { useRef, useState } from 'react';

export default function FAQPage() {
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState('overview');
  const sectionRefs = useRef({});

  // Content data based on user's provided FAQ & User Guide
  const sectionsData = [
    {
      id: 'overview',
      title: '🧩 Overview',
      blocks: [
        { type: 'p', text: 'The Expense Management System streamlines the entire expense process — from creation to approval, payment, and goods receipt — ensuring transparency and accountability at every stage.' },
        { type: 'p', text: 'There are four main roles in the system:' },
        { type: 'ul', items: [
          'Creator – Creates and submits expenses with vendor details.',
          'Approver – Reviews, approves, or rejects expenses at multiple levels.',
          'Payment Team – Handles payment processing and uploads payment receipts.',
          'Admin – Has full access to manage, monitor, and view all operations.'
        ]}
      ]
    },
    {
      id: 'create',
      title: '📝 1. Creating a New Expense',
      blocks: [
        { type: 'p', text: 'Q1. Who can create an expense?' },
        { type: 'p', text: 'Only users with the Creator role can create new expenses.' },
        { type: 'p', text: 'Q2. What information is required to create an expense?' },
        { type: 'ul', items: [
          'Expense Name', 'Company', 'Date', 'Department', 'Region', 'Expense Group', 'Expense Category'
        ]},
        { type: 'p', text: 'Q3. What happens after creating the expense?' },
        { type: 'ul', items: [
          'A unique Expense ID is generated and you are redirected to the Expense Details page.',
          'Add Vendor Details.',
          'Add Expense Items (categorized as Inventory or Non-Inventory).',
          'Specify quantity for each item (verified later during Goods Receipt).'
        ]}
      ]
    },
    {
      id: 'vendors-items',
      title: '🏷️ 2. Adding Vendor Details & Expense Items',
      blocks: [
        { type: 'p', text: 'Q4. Can I add multiple vendors for a single expense?' },
        { type: 'p', text: 'Yes, you can add multiple vendor quotations for comparison and selection during the approval process.' },
        { type: 'p', text: 'Q5. What information should be included in expense items?' },
        { type: 'ul', items: [ 'Item Name', 'Description', 'Quantity', 'Unit Price', 'Type (Inventory / Non-Inventory)' ]}
      ]
    },
    {
      id: 'approval',
      title: '✅ 3. Expense Approval Process',
      blocks: [
        { type: 'p', text: 'Q6. What happens after submitting the expense for approval?' },
        { type: 'p', text: 'Once all details are filled and submitted, the expense moves to the approval workflow, depending on the expense type (Admin or IT).' },
        { type: 'p', text: 'Q7. What is the approval hierarchy?' },
        { type: 'ul', items: [
          'Admin Expense — Level 1: Dr. Kishor Dholwani → Level 2: Chetan Patel → Level 3: Pragnesh Patel',
          'IT Expense — Level 1: Pragnesh Patel → Level 2: Chetan Patel → Level 3: Dr. Kishor Dholwani'
        ]},
        { type: 'p', text: 'Q8. What does an approver see on their screen?' },
        { type: 'ul', items: [
          'Expense Details (name, company, department, region, etc.)',
          'Vendor Details', 'Approval History', 'Discussion Panel for comments and replies', 'Approve / Reject with comment options'
        ]},
        { type: 'p', text: 'Q9. How is the vendor selected?' },
        { type: 'ul', items: [
          'The first approver selects the vendor quote to be approved.',
          'After selection, the vendor is locked; the next approvers can only approve or reject.'
        ]},
        { type: 'p', text: 'Q10. How do discussions and comments work?' },
        { type: 'ul', items: [
          'Approvers can comment if they need clarification.',
          'The Creator receives an email when a comment is added.',
          'Creator can reply in the same thread; approvers get notified of replies.',
          'This ensures transparent and traceable communication.'
        ]},
        { type: 'p', text: 'Q11. What happens if an expense is rejected?' },
        { type: 'ul', items: [
          'When any approver rejects, all previous approvers and the Creator receive an email notification.',
          'Once rejected, the expense cannot be resubmitted — the Creator must create a new expense (new Expense ID).'
        ]}
      ]
    },
    {
      id: 'adjustment',
      title: '💰 4. Payment Adjustment & Approval',
      blocks: [
        { type: 'p', text: 'Q12. What happens after the final approval?' },
        { type: 'p', text: 'Once fully approved, the expense appears in the Payment Adjustment section.' },
        { type: 'p', text: 'Q13. What does the Creator do in Payment Adjustment?' },
        { type: 'ul', items: [
          'Open the approved expense.', 'Update the actual amount to be paid.', 'Click Update Amount.',
          'If the amount changed → send for Level 3 (L3) approval again.',
          'If unchanged → it is forwarded directly to the Payment Team.'
        ]},
        { type: 'p', text: 'Q14. Who gives the L3 approval?' },
        { type: 'p', text: 'Only Dr. Kishor Dholwani gives L3 approval — but only in cases where the payment amount was modified.' }
      ]
    },
    {
      id: 'payment',
      title: '💳 5. Payment Management',
      blocks: [
        { type: 'p', text: 'Q15. What does the Payment Team do?' },
        { type: 'ul', items: [
          'Processes approved payments.',
          'Uploads the payment receipt or proof of payment.',
          'Updates amount, date, and transaction reference number.'
        ]},
        { type: 'p', text: 'Q16. What happens after payment?' },
        { type: 'p', text: 'Once payment is made and the receipt is uploaded, the expense status changes to “Payment Done.”' }
      ]
    },
    {
      id: 'receipt',
      title: '📦 6. Goods Receipt',
      blocks: [
        { type: 'p', text: 'Q17. When does an expense appear in the Goods Receipt section?' },
        { type: 'p', text: 'All approved expenses automatically appear in the Goods Receipt module.' },
        { type: 'p', text: 'Q18. Can I record goods receipt before payment?' },
        { type: 'p', text: 'Yes, goods can be received and recorded before or without payment, depending on the purchase conditions.' },
        { type: 'p', text: 'Q19. How to record a goods receipt?' },
        { type: 'ul', items: [
          'Click Add Receipt on the respective expense.',
          'Select the relevant items.',
          'Enter the Received Quantity and add notes for discrepancies (e.g., ordered 15, received 10 → “5 pending – vendor confirmed later delivery”).',
          'If remaining quantities are expected → Save.',
          'If no further goods are expected → Submit to close the receipt.'
        ]},
        { type: 'p', text: 'Q20. What does “Closed Receipt” mean?' },
        { type: 'p', text: 'It means all items have been received, or the receipt is finalized with no pending quantities.' }
      ]
    },
    {
      id: 'lists-dash',
      title: '🧾 7. Expense List & Dashboard',
      blocks: [
        { type: 'p', text: 'Q21. Where can I see all my expenses?' },
        { type: 'ul', items: [
          'Go to Expense List to view: Expense ID, Name, Status (Pending, Approved, Rejected, Payment Done).',
          'Use filters for Department, Category, or Date.'
        ]},
        { type: 'p', text: 'Q22. What does the Dashboard show?' },
        { type: 'ul', items: [
          'Total Expenses', 'Pending Approvals', 'Approved Expenses', 'Total Value of Expenses',
          'Each dashboard card is clickable and opens the relevant filtered list.'
        ]}
      ]
    },
    {
      id: 'roles',
      title: '🧑‍💼 8. Roles & Permissions Summary',
      blocks: [
        { type: 'ul', items: [
          'Creator — Creates expenses and adds details. Key permissions: Add vendors, items, submit for approval.',
          'Approver — Reviews, approves, or rejects. Key permissions: View details, approve/reject, comment.',
          'Payment Team — Processes payments. Key permissions: Upload receipts, mark as “Payment Done”.',
          'Admin — Full system access. Key permissions: Manage users, approvals, and view all records.'
        ]}
      ]
    },
    {
      id: 'emails',
      title: '🔔 9. Email Notifications',
      blocks: [
        { type: 'p', text: 'Q23. When are emails triggered?' },
        { type: 'ul', items: [
          'New expense sent for approval', 'Expense approved or rejected', 'New comment added or replied to', 'Expense amount updated', 'Payment completed'
        ]},
        { type: 'p', text: 'Q24. Can I access the expense directly from email?' },
        { type: 'p', text: 'Yes, each notification email includes a direct link to the related expense page for quick access.' }
      ]
    },
    {
      id: 'tips',
      title: '🧠 10. Common Issues & Tips',
      blocks: [
        { type: 'p', text: 'Q25. I can’t find my expense after creating it.' },
        { type: 'p', text: 'Ensure you clicked Save after adding vendor and item details. Drafts aren’t visible in the main list.' },
        { type: 'p', text: 'Q26. My expense shows “Vendor Locked.”' },
        { type: 'p', text: 'The first approver has already selected a vendor. You can only review and approve/reject.' },
        { type: 'p', text: 'Q27. Expense isn’t appearing in Payment Adjustment.' },
        { type: 'p', text: 'Verify that all approval levels are completed and the expense is marked as Approved.' },
        { type: 'p', text: 'Q28. Can I edit a rejected expense?' },
        { type: 'p', text: 'No. Once rejected, you must create a new expense with a new Expense ID.' }
      ]
    }
  ];

  // Utility: strip leading emoji from a title (for TOC display only)
  const stripEmoji = (title) => title.replace(/^[^A-Za-z0-9]+\s*/, '');

  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const highlight = (text) => {
    if (!query) return text;
    const re = new RegExp(`(${escapeRegExp(query)})`, 'ig');
    return text.replace(re, '<mark style="background-color: var(--primary); color: var(--panel); padding: 1px 2px; border-radius: 2px;">$1</mark>');
  };

  const blockHasMatch = (block) => {
    if (!query) return true;
    const q = query.toLowerCase();
    if (block.type === 'p') return block.text.toLowerCase().includes(q);
    if (block.type === 'ul') return block.items.some((i) => i.toLowerCase().includes(q));
    return false;
  };

  const sectionHasMatch = (section) => section.blocks.some(blockHasMatch);

  const renderBlock = (block, idx) => {
    if (block.type === 'p') {
      return (
        <p key={idx} style={{ color: 'var(--text)', margin: '0.5rem 0' }} dangerouslySetInnerHTML={{ __html: highlight(block.text) }} />
      );
    }
    if (block.type === 'ul') {
      const items = !query
        ? block.items
        : block.items.filter((i) => i.toLowerCase().includes(query.toLowerCase()));
      if (query && items.length === 0) return null;
      return (
        <ul key={idx} className="list-disc pl-5" style={{ color: 'var(--text)' }}>
          {items.map((it, i) => (
            <li key={i} style={{ color: 'var(--text)', margin: '0.25rem 0' }} dangerouslySetInnerHTML={{ __html: highlight(it) }} />
          ))}
        </ul>
      );
    }
    return null;
  };

  // We render a single selected section; no scroll tracking needed.

  // Navigate to a section and scroll to top
  const goTo = (id) => {
    setActiveId(id);
    const el = sectionRefs.current['contentTop'];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="p-6 w-full space-y-6" style={{ color: 'var(--text)' }}>
      <div>
        <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--heading-color, var(--text))' }}>💼 Expense Management System – FAQ & User Guide</h1>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>Comprehensive user guide, role workflows, and frequently asked questions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        <aside className="hidden md:block">
          <div className="sticky top-4 p-3 rounded-lg border" style={{ borderColor: 'var(--border)', color: 'var(--text)', backgroundColor: 'var(--panel)' }}>
            <div className="text-xs font-semibold mb-2" style={{ color: 'var(--muted)' }}>On this page</div>
            <nav className="space-y-1">
              {sectionsData.map((s) => (
                <button
                  key={s.id}
                  onClick={() => goTo(s.id)}
                  className="w-full text-left px-2.5 py-1.5 rounded-md transition text-sm hover:opacity-90"
                  style={{
                    color: activeId === s.id ? 'var(--accent)' : 'var(--text)',
                    fontWeight: activeId === s.id ? 600 : 400,
                    backgroundColor: activeId === s.id ? 'rgba(140, 33, 30, 0.18)' : 'transparent'
                  }}
                >
                  {stripEmoji(s.title)}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        <div>
          {/* <div ref={(el) => (sectionRefs.current['contentTop'] = el)} className="mb-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search FAQs..."
              className="w-full px-4 py-2 border rounded-md"
              style={{ background: 'var(--panel)', borderColor: 'var(--border)', color: 'var(--text)' }}
            />
          </div> */}
          {(() => {
            const current = sectionsData.find((s) => s.id === activeId) || sectionsData[0];
            const blocks = query ? current.blocks.filter(blockHasMatch) : current.blocks;
            return (
              <section className="rounded-lg border shadow-sm" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--panel)' }}>
                <div className="px-4 py-3 border-b rounded-t-lg" style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
                  <h2 className="text-base font-semibold">{current.title}</h2>
                </div>
                <div className="px-5 pb-4 pt-3 text-sm leading-6 space-y-3 faq-content" style={{ color: 'var(--text)' }}>
                  {blocks.map((b, idx) => renderBlock(b, idx))}
                </div>
              </section>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
